import { Box, CircularProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ApexOptions } from 'apexcharts';
import { useGetRenkiCount, useGetSimpleSACount } from 'gen/client';
import { AnalyticsGetRenkiCountResponseDTO, AnalyticsGetSimpleSACountResponseDTO } from 'gen/model';
import { useMemo } from 'react';
import Chart from 'react-apexcharts';

import { useGetInstanceInfo } from 'kerberus-components/src/Hooks/useGetInstanceInfo';

import { dayjs } from '../../Dayjs/dayjs-utils';
interface CandlestickData {
    fillColor?: string;
    strokeColor?: string;
    x: Date;
    y: [number, number, number, number]; // [open, high, low, close]
}

interface LineData {
    x: Date;
    y: number | null;
}

interface NormalizedDataPoint {
    avg: number;
    close: number;
    high: number;
    isDecrease: boolean;
    isIncrease: boolean;
    low: number;
    max: number;
    min: number;
    open: number;
    timestamp: Date;
}

interface ChartSeriesItem {
    data: LineData[] | CandlestickData[];
    name: string;
    type: string;
}

export interface InfoscreenActivityChartConfig {
    chartType?: 'line' | 'area' | 'candlestick';
    dataSource: 'simple' | 'renki';
    interval: 'hour' | 'day' | 'week';
    siteId: string;
    timeFilter: '1h' | '24h' | '7d';
    title?: string;
}

interface InfoscreenActivityChartProps {
    config: InfoscreenActivityChartConfig;
    infoscreenLayout?: {
        backgroundColor?: string;
        textColor?: string;
        titleColor?: string;
    };
}

export default function InfoscreenActivityChart({ config, infoscreenLayout }: Readonly<InfoscreenActivityChartProps>) {
    const theme = useTheme();
    const { instanceInfo } = useGetInstanceInfo();

    // Check if we have a valid siteId
    const hasValidSiteId = Boolean(config.siteId && config.siteId !== '');

    const apiParams = useMemo(
        () => ({
            instance_role: instanceInfo.instance_role || 'dev',
            interval: config.interval,
            site_id: config.siteId,
        }),
        [instanceInfo.instance_role, config.interval, config.siteId],
    );

    const { data: SimpleCount = [], isLoading: isLoadingSimple } = useGetSimpleSACount(apiParams, {
        swr: {
            dedupingInterval: 60_000,
            enabled: hasValidSiteId && config.dataSource === 'simple',
            refreshInterval: 120_000,
            revalidateOnFocus: false,
        },
    });

    const { data: renkiData = [], isLoading: isLoadingRenki } = useGetRenkiCount(apiParams, {
        swr: {
            dedupingInterval: 60_000,
            enabled: hasValidSiteId && config.dataSource === 'renki',
            refreshInterval: 120_000,
            revalidateOnFocus: false,
        },
    });

    // Early return if no valid siteId is provided (after hooks)

    // Set the data based on selected data source
    const SACountData = useMemo(() => {
        if (config.dataSource === 'simple') {
            return SimpleCount as unknown as AnalyticsGetSimpleSACountResponseDTO[];
        } else {
            return renkiData as unknown as AnalyticsGetRenkiCountResponseDTO[];
        }
    }, [config.dataSource, SimpleCount, renkiData]);

    const isLoading = config.dataSource === 'simple' ? isLoadingSimple : isLoadingRenki;

    const filterOptions = useMemo(
        () => [
            { hours: 1, label: 'Last Hour', value: '1h' },
            { hours: 24, label: 'Last 24 Hours', value: '24h' },
            { hours: 168, label: 'Last 7 Days', value: '7d' },
        ],
        [],
    );

    const processedData = useMemo(() => {
        let previousAvg: number | null = null;
        const normalizedData: NormalizedDataPoint[] = [];

        if (config.dataSource === 'simple') {
            // Process SimpleCount data
            const typedData = SACountData as AnalyticsGetSimpleSACountResponseDTO[];
            typedData.forEach((point) => {
                const avg = point.avg_data + point.avg_probe;
                const min = point.min_data + point.min_probe;
                const max = point.max_data + point.max_probe;

                const isIncrease = previousAvg !== null && avg > previousAvg;
                const isDecrease = previousAvg !== null && avg < previousAvg;

                const open = previousAvg || avg;
                const close = avg;

                previousAvg = avg;

                normalizedData.push({
                    avg,
                    close,
                    high: max,
                    isDecrease,
                    isIncrease,
                    low: min,
                    max,
                    min,
                    open,
                    timestamp: new Date(point.timestamp),
                });
            });
        } else {
            // Process RenkiCount data
            const typedData = SACountData;
            typedData.forEach((point) => {
                const avg = point.avg_total;
                const min = point.min_total;
                const max = point.max_total;

                const isIncrease = previousAvg !== null && avg > previousAvg;
                const isDecrease = previousAvg !== null && avg < previousAvg;

                const open = previousAvg || avg;
                const close = avg;

                previousAvg = avg;

                normalizedData.push({
                    avg,
                    close,
                    high: max,
                    isDecrease,
                    isIncrease,
                    low: min,
                    max,
                    min,
                    open,
                    timestamp: new Date(point.timestamp),
                });
            });
        }

        return normalizedData;
    }, [SACountData, config.dataSource]);

    const { filteredData, timeRange } = useMemo(() => {
        const now = dayjs();
        const selectedOption = filterOptions.find((option) => option.value === config.timeFilter);
        const hours = selectedOption?.hours || 24;
        const cutoff = now.subtract(hours, 'hour');

        const min = cutoff.valueOf();
        const max = now.valueOf();

        const filteredResult = processedData.filter((item) => dayjs(item.timestamp).isAfter(cutoff));

        return {
            filteredData: filteredResult,
            timeRange: { max, min },
        };
    }, [filterOptions, processedData, config.timeFilter]);

    // Process data for chart display
    const { candlestickData, lineData } = useMemo(() => {
        if (filteredData.length === 0) {
            return { candlestickData: [], lineData: [] };
        }

        const resultLine: LineData[] = [];
        const resultCandlestick: CandlestickData[] = [];

        filteredData.forEach((point) => {
            const currentTimestamp = new Date(point.timestamp);

            // Add the current data point for both series
            resultLine.push({
                x: currentTimestamp,
                y: point.avg,
            });

            resultCandlestick.push({
                fillColor: point.isIncrease ? '#26a69a' : point.isDecrease ? '#ef5350' : '#b0bec5',
                strokeColor: point.isIncrease ? '#26a69a' : point.isDecrease ? '#ef5350' : '#b0bec5',
                x: currentTimestamp,
                y: [point.open, point.high, point.low, point.close],
            });
        });

        return { candlestickData: resultCandlestick, lineData: resultLine };
    }, [filteredData]);

    // Chart options optimized for fullscreen infoscreen display
    const options: ApexOptions = {
        chart: {
            background: 'transparent',
            id: 'infoscreen-activity-chart',
            toolbar: {
                show: false, // Hide toolbar for clean infoscreen look
            },
            type: config.chartType === 'candlestick' ? 'candlestick' : 'line',
            zoom: {
                enabled: false, // Disable zoom for infoscreen
            },
        },
        colors: ['#008FFB', '#00E396', '#FEB019', '#FF4560', '#775DD0'],
        grid: {
            borderColor: infoscreenLayout?.textColor ? `${infoscreenLayout.textColor}20` : theme.palette.divider,
            padding: {
                bottom: 20,
                left: 20,
                right: 30,
                top: 20,
            },
            position: 'back',
            show: true,
            strokeDashArray: 2,
        },
        legend: {
            fontFamily: theme.typography.fontFamily,
            fontSize: '18px',
            horizontalAlign: 'center',
            itemMargin: {
                horizontal: 20,
                vertical: 10,
            },
            labels: {
                colors: infoscreenLayout?.textColor || theme.palette.text.primary,
            },
            position: 'top',
            show: true,
        },
        markers: {
            size: 0,
        },
        noData: {
            align: 'center',
            style: {
                color: infoscreenLayout?.textColor || theme.palette.text.primary,
                fontFamily: theme.typography.fontFamily,
                fontSize: '24px',
            },
            text: 'No data available',
            verticalAlign: 'middle',
        },
        stroke: {
            curve: 'smooth',
            width: [3, 2], // Thicker lines for better visibility on large screens
        },
        theme: {
            mode: 'light',
        },
        title: {
            align: 'center',
            margin: 40,
            style: {
                color: infoscreenLayout?.titleColor || theme.palette.text.primary,
                fontFamily: theme.typography.fontFamily,
                fontSize: '28px',
                fontWeight: 'bold',
            },
            text: config.title || (config.dataSource === 'simple' ? 'Simple Activity Count' : 'Renki Activity Count'),
        },
        tooltip: {
            enabled: true,
            intersect: false,
            shared: true,
            style: {
                fontFamily: theme.typography.fontFamily,
                fontSize: '14px',
            },
            theme: 'light',
        },
        xaxis: {
            axisBorder: {
                color: infoscreenLayout?.textColor || theme.palette.divider,
                show: true,
            },
            axisTicks: {
                color: infoscreenLayout?.textColor || theme.palette.divider,
                show: true,
            },
            labels: {
                datetimeUTC: false,
                style: {
                    colors: infoscreenLayout?.textColor || theme.palette.text.secondary,
                    fontFamily: theme.typography.fontFamily,
                    fontSize: '16px',
                },
            },
            max: timeRange.max,
            min: timeRange.min,
            type: 'datetime',
        },
        yaxis: {
            forceNiceScale: true,
            labels: {
                style: {
                    colors: infoscreenLayout?.textColor || theme.palette.text.secondary,
                    fontFamily: theme.typography.fontFamily,
                    fontSize: '16px',
                },
            },
        },
    };

    // Chart series based on selected chart type
    const series = useMemo(() => {
        const chartType = config.chartType || 'line';
        const seriesData: ChartSeriesItem[] = [];

        if (chartType === 'candlestick') {
            // For candlestick charts, only show candlestick data (primarily for Simple data)
            if (config.dataSource === 'simple') {
                seriesData.push({
                    data: candlestickData as unknown as LineData[],
                    name: 'Min/Max Range',
                    type: 'candlestick',
                });
            } else {
                // For Renki data, fall back to line chart since candlestick needs OHLC data
                seriesData.push({
                    data: lineData,
                    name: 'Average',
                    type: 'line',
                });
            }
        } else if (chartType === 'area') {
            // Area chart with the average line data
            seriesData.push({
                data: lineData,
                name: 'Average',
                type: 'area',
            });
        } else {
            // Default line chart
            seriesData.push({
                data: lineData,
                name: 'Average',
                type: 'line',
            });
        }

        return seriesData;
    }, [candlestickData, lineData, config.dataSource, config.chartType]);

    if (isLoading) {
        return (
            <Box
                sx={{
                    alignItems: 'center',
                    backgroundColor: infoscreenLayout?.backgroundColor || 'transparent',
                    display: 'flex',
                    height: '100%',
                    justifyContent: 'center',
                    width: '100%',
                }}
            >
                <CircularProgress
                    size={80}
                    sx={{
                        color: infoscreenLayout?.textColor || theme.palette.primary.main,
                    }}
                />
            </Box>
        );
    }

    if (!hasValidSiteId) {
        return (
            <Box
                sx={{
                    alignItems: 'center',
                    backgroundColor: infoscreenLayout?.backgroundColor || 'transparent',
                    display: 'flex',
                    height: '100%',
                    justifyContent: 'center',
                    width: '100%',
                }}
            >
                <Typography
                    sx={{
                        color: infoscreenLayout?.textColor || theme.palette.text.primary,
                        fontSize: '20px',
                        textAlign: 'center',
                    }}
                >
                    No site selected for chart
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                backgroundColor: infoscreenLayout?.backgroundColor || 'transparent',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden', // Prevent overflow issues on large screens
                width: '100%',
            }}
        >
            <Chart
                height="100%"
                options={options}
                series={series}
                type={config.chartType === 'candlestick' ? 'candlestick' : 'line'}
                width="100%"
            />
        </Box>
    );
}
